import { AppConfig } from '@core/domain/AppConfig.ts';
import {
  LOCAL_MEDIA_IDB_STORE,
  openSharedLocalIdb,
} from '@core/infrastructure/driven/indexedDb/sharedLocalIdb.ts';
import { getLocalSqliteIdbBytes } from '@src/modules/platform/local-sqlite/infrastructure/sqliteIdbStorage.ts';
import type { GetSqlJsDatabase } from '@src/modules/platform/local-sqlite/infrastructure/sqlJsRowHelpers.ts';
import type { LocalStorageDiagnostics } from '../../domain/ports/SystemDiagnosticsPort.ts';

const MEDIA_READY_PROBE_KEY = '__esan_media_ready_probe__';
const MB = 1024 * 1024;

export const EMPTY_LOCAL_STORAGE_DIAGNOSTICS: LocalStorageDiagnostics = {
  quotaBytes: null,
  usageBytes: null,
  sqliteBytes: 0,
  imageBytes: 0,
  signatureBytes: 0,
};

export function formatStorageMegabytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  if (bytes < MB) return '< 1 MB';
  return `${(bytes / MB).toFixed(bytes >= 10 * MB ? 0 : 1)} MB`;
}

function isStoredBlob(value: unknown): value is Blob {
  return (
    value instanceof Blob ||
    (typeof value === 'object' &&
      value !== null &&
      typeof (value as Blob).arrayBuffer === 'function' &&
      typeof (value as Blob).size === 'number')
  );
}

function readNonEmptyIds(db: import('sql.js').Database, sql: string): Set<string> {
  const ids = new Set<string>();
  try {
    const stmt = db.prepare(sql);
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const id = typeof row.id === 'string' ? row.id.trim() : '';
      if (id) ids.add(id);
    }
    stmt.free();
  } catch {
    /* tables may be missing before first seed */
  }
  return ids;
}

const SIGNATURE_MEDIA_SQL = `
  SELECT TRIM(firma_media_id) AS id FROM entrega_participantes WHERE TRIM(firma_media_id) != ''
  UNION SELECT TRIM(firma_recibe_media_id) FROM entrega_remisiones WHERE TRIM(firma_recibe_media_id) != ''
  UNION SELECT TRIM(firma_entrega_media_id) FROM entrega_remisiones WHERE TRIM(firma_entrega_media_id) != ''
  UNION SELECT TRIM(firma_media_id) FROM reposiciones_productos
    WHERE firma_media_id IS NOT NULL AND TRIM(firma_media_id) != ''
`;

const IMAGE_MEDIA_SQL = `
  SELECT TRIM(foto_media_id) AS id FROM entrega_participantes WHERE TRIM(foto_media_id) != ''
  UNION SELECT TRIM(cedula_beneficiario_front_media_id) FROM entrega_participantes
    WHERE TRIM(cedula_beneficiario_front_media_id) != ''
  UNION SELECT TRIM(cedula_beneficiario_back_media_id) FROM entrega_participantes
    WHERE TRIM(cedula_beneficiario_back_media_id) != ''
  UNION SELECT TRIM(cedula_autorizado_front_media_id) FROM entrega_participantes
    WHERE TRIM(cedula_autorizado_front_media_id) != ''
  UNION SELECT TRIM(cedula_autorizado_back_media_id) FROM entrega_participantes
    WHERE TRIM(cedula_autorizado_back_media_id) != ''
  UNION SELECT TRIM(documento_autorizacion_media_id) FROM entrega_participantes
    WHERE TRIM(documento_autorizacion_media_id) != ''
  UNION SELECT TRIM(foto_media_id) FROM entrega_remisiones WHERE TRIM(foto_media_id) != ''
  UNION SELECT TRIM(foto_media_id) FROM reposiciones_productos
    WHERE foto_media_id IS NOT NULL AND TRIM(foto_media_id) != ''
`;

export function queryReferencedMediaIds(getDb: GetSqlJsDatabase): {
  signatureIds: Set<string>;
  imageIds: Set<string>;
} {
  const db = getDb();
  if (!db) return { signatureIds: new Set(), imageIds: new Set() };
  const signatureIds = readNonEmptyIds(db, SIGNATURE_MEDIA_SQL);
  const imageIds = readNonEmptyIds(db, IMAGE_MEDIA_SQL);
  for (const id of signatureIds) imageIds.delete(id);
  return { signatureIds, imageIds };
}

async function readStorageEstimate(): Promise<Pick<LocalStorageDiagnostics, 'quotaBytes' | 'usageBytes'>> {
  try {
    const estimate = await globalThis.navigator?.storage?.estimate?.();
    const quotaBytes = typeof estimate?.quota === 'number' && Number.isFinite(estimate.quota) ? estimate.quota : null;
    const usageBytes = typeof estimate?.usage === 'number' && Number.isFinite(estimate.usage) ? estimate.usage : null;
    return { quotaBytes, usageBytes };
  } catch {
    return { quotaBytes: null, usageBytes: null };
  }
}

async function sumSqlitePersistedBytes(): Promise<number> {
  try {
    const bytes = await getLocalSqliteIdbBytes();
    return bytes?.byteLength ?? 0;
  } catch {
    return 0;
  }
}

async function sumRecoveryBackupBytes(): Promise<number> {
  const store = AppConfig.getLocalSqliteIdbStore();
  const backupKey = `${AppConfig.getLocalSqliteIdbSqliteKey()}.recovery-backup`;
  try {
    const idb = await openSharedLocalIdb();
    return await new Promise<number>((resolve, reject) => {
      const tx = idb.transaction(store, 'readonly');
      const req = tx.objectStore(store).get(backupKey);
      req.onsuccess = () => {
        const value = req.result;
        resolve(value instanceof Uint8Array ? value.byteLength : 0);
      };
      req.onerror = () => reject(req.error ?? new Error('indexedDB.get failed'));
      tx.onerror = () => reject(tx.error ?? new Error('indexedDB transaction failed'));
    });
  } catch {
    return 0;
  }
}

async function sumMediaBlobBytes(
  signatureIds: Set<string>,
  imageIds: Set<string>,
): Promise<Pick<LocalStorageDiagnostics, 'signatureBytes' | 'imageBytes'>> {
  if (signatureIds.size === 0 && imageIds.size === 0) {
    return { signatureBytes: 0, imageBytes: 0 };
  }
  let signatureBytes = 0;
  let imageBytes = 0;
  try {
    const idb = await openSharedLocalIdb();
    await new Promise<void>((resolve, reject) => {
      const tx = idb.transaction(LOCAL_MEDIA_IDB_STORE, 'readonly');
      const store = tx.objectStore(LOCAL_MEDIA_IDB_STORE);
      const req = store.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return;
        const key = String(cursor.key);
        if (key === MEDIA_READY_PROBE_KEY) {
          cursor.continue();
          return;
        }
        const value = cursor.value;
        const size = isStoredBlob(value) ? value.size : 0;
        if (signatureIds.has(key)) signatureBytes += size;
        else if (imageIds.has(key)) imageBytes += size;
        cursor.continue();
      };
      req.onerror = () => reject(req.error ?? new Error('indexedDB cursor failed'));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('indexedDB transaction failed'));
    });
  } catch {
    return { signatureBytes: 0, imageBytes: 0 };
  }
  return { signatureBytes, imageBytes };
}

export async function queryLocalStorageDiagnostics(getDb?: GetSqlJsDatabase): Promise<LocalStorageDiagnostics> {
  const [{ quotaBytes, usageBytes }, sqliteMainBytes, sqliteBackupBytes, media] = await Promise.all([
    readStorageEstimate(),
    sumSqlitePersistedBytes(),
    sumRecoveryBackupBytes(),
    getDb
      ? (() => {
          const { signatureIds, imageIds } = queryReferencedMediaIds(getDb);
          return sumMediaBlobBytes(signatureIds, imageIds);
        })()
      : Promise.resolve({ signatureBytes: 0, imageBytes: 0 }),
  ]);
  return {
    quotaBytes,
    usageBytes,
    sqliteBytes: sqliteMainBytes + sqliteBackupBytes,
    imageBytes: media.imageBytes,
    signatureBytes: media.signatureBytes,
  };
}
